import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Check, Eye, Package, PackagePlus, Pencil, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Select } from '@/shared/ui/selects/Select';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { FulfillmentChip } from '@/shared/ui/chips/FulfillmentChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { useParcelsList } from '@/entities/parcels';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { hasPerm, usePerm, usePermsRoot } from '@/shared/hooks/usePerm';
import {
  FeaturesParcelsApproveDialog,
  FeaturesParcelsDeleteDialog,
  FeaturesParcelsRejectDialog,
} from '@/features/parcels';
import { formatDate } from '@/shared/utils/date';
import { formatUsd } from '@/shared/utils/currency';
import {
  PARCEL_STATUS_VALUES,
  type ApiParcelsGetItem,
  type ParcelStatus,
} from '@/shared/types/api/parcels';
import type { SplitStatus } from '@/shared/types/api/splits';

const PAGE_SIZE = 20;

// Local augmentation: the parcels list API now returns split-related flags
// (#__, 2026-06-14) but the shared `ApiParcelsGetItem` type cannot be edited
// from here, so the extra read-only fields are declared inline and applied via
// a narrow cast inside the dedicated column cell.
type ParcelWithSplit = ApiParcelsGetItem & {
  is_split?: boolean;
  split_status?: SplitStatus | null;
  delivered_to_customer?: boolean;
};

export const WidgetsParcelsMain = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const STATUS_OPTIONS = useMemo(
    () => [
      { value: '', label: t('parcels.list.allStatuses') },
      ...PARCEL_STATUS_VALUES.map((v) => ({ value: v, label: t(`parcelStatus.${v}`) })),
    ],
    [t],
  );

  const DEBT_OPTIONS = useMemo(
    () => [
      { value: '', label: t('parcels.list.debtFilterAll') },
      { value: 'true', label: t('parcels.list.debtor') },
      { value: 'false', label: t('parcels.list.noDebt') },
    ],
    [t],
  );

  // Permissions (super bypass handled inside `usePerm`). `permsRoot` is used
  // by the column builder via `hasPerm` so the memoized `columns` array stays
  // a single hook call instead of N conditional ones.
  const permsRoot = usePermsRoot();
  const canCreate = usePerm('parcels.create');
  const canEdit = usePerm('parcels.edit');
  const canDelete = usePerm('parcels.delete');
  const canApprove = usePerm('parcels.detail.approve');
  const canReject = usePerm('parcels.detail.reject');
  const canViewDetail = usePerm('parcels.detail');

  // Filters
  const canFilterSearch = usePerm('parcels.list.filter.search');
  const canFilterStatus = usePerm('parcels.list.filter.status');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ParcelStatus | ''>('');
  const [hasDebt, setHasDebt] = useState<'' | 'true' | 'false'>('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const [approveTarget, setApproveTarget] = useState<ApiParcelsGetItem | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApiParcelsGetItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiParcelsGetItem | null>(null);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      // Only ship filter values the user is allowed to use. Otherwise a
      // hidden search/status input could still leak via stored state.
      search: canFilterSearch ? debouncedSearch.trim() || undefined : undefined,
      status: canFilterStatus ? status || undefined : undefined,
      has_debt: hasDebt || undefined,
    }),
    [page, debouncedSearch, status, canFilterSearch, canFilterStatus, hasDebt],
  );

  const query = useParcelsList(params);

  const columns = useMemo<ColumnDef<ApiParcelsGetItem>[]>(() => {
    // Build column list from the registry then filter by the per-column perm.
    // A column with `perm: null` is always shown (here: the synthetic
    // `actions` column).
    const all: { col: ColumnDef<ApiParcelsGetItem>; perm: string | null }[] = [
      {
        perm: 'parcels.list.column.trek_id',
        col: {
          accessorKey: 'trek_id',
          header: t('parcels.list.columns.trekId'),
          cell: (info) => <span className="font-mono text-xs">{info.row.original.trek_id}</span>,
        },
      },
      {
        perm: 'parcels.list.column.cargo_id_code',
        col: {
          accessorKey: 'cargo_id_code',
          header: t('parcels.list.columns.cargoId'),
          cell: (info) =>
            info.row.original.cargo_id_code ? (
              <span className="font-mono text-xs">{info.row.original.cargo_id_code}</span>
            ) : (
              <span className="text-ink-muted">—</span>
            ),
        },
      },
      {
        perm: 'parcels.list.column.name',
        col: {
          accessorKey: 'name',
          header: t('parcels.list.columns.name'),
          cell: (info) => (
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-ink">{info.row.original.name}</span>
              {info.row.original.store_name && (
                <span className="truncate text-xs text-ink-muted">{info.row.original.store_name}</span>
              )}
            </div>
          ),
        },
      },
      {
        perm: 'parcels.list.column.fulfillment_status',
        col: {
          id: 'fulfillment',
          header: t('parcels.list.columns.fulfillment'),
          enableSorting: false,
          cell: (info) => {
            const row = info.row.original as ParcelWithSplit;
            if (row.delivered_to_customer) {
              return (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-emerald-800">
                  <Check className="h-3 w-3" />
                  {t('parcels.list.delivered')}
                </span>
              );
            }
            return row.fulfillment_status ? (
              <FulfillmentChip status={row.fulfillment_status} />
            ) : (
              <span className="text-ink-muted">—</span>
            );
          },
        },
      },
      {
        // Split / customer-delivery indicators. No dedicated registry perm,
        // so always shown (matches the `total` column).
        perm: null,
        col: {
          id: 'split',
          header: t('parcels.list.columns.split'),
          enableSorting: false,
          cell: (info) => {
            const row = info.row.original as ParcelWithSplit;
            return row.is_split ? (
              <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-violet-800">
                {t('parcels.list.splitBadge')}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-slate-700">
                {t('parcels.list.notSplitBadge')}
              </span>
            );
          },
        },
      },
      {
        // Customer-debt indicator (2026-06-28). No dedicated registry perm,
        // so always shown (matches the `split` column).
        perm: null,
        col: {
          id: 'has_debt',
          header: t('parcels.list.columns.hasDebt'),
          enableSorting: false,
          cell: (info) =>
            info.row.original.has_debt ? (
              <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-rose-800">
                {t('parcels.list.debtor')}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap text-emerald-800">
                {t('parcels.list.noDebt')}
              </span>
            ),
        },
      },
      {
        // No dedicated PINFL column permission in the registry yet — gate it
        // under the closest neighbour (`region`) for now; a super sees it.
        perm: 'parcels.list.column.region',
        col: {
          accessorKey: 'pinfl',
          header: t('parcels.list.columns.pinfl'),
          cell: (info) => <span className="font-mono text-xs">{info.row.original.pinfl}</span>,
        },
      },
      {
        perm: null,
        col: {
          accessorKey: 'total_amount',
          header: t('parcels.list.columns.total'),
          cell: (info) => <span className="font-medium">{formatUsd(info.row.original.total_amount)}</span>,
        },
      },
      {
        perm: 'parcels.list.column.created_at',
        col: {
          accessorKey: 'created_at',
          header: t('parcels.list.columns.date'),
          cell: (info) => formatDate(info.row.original.created_at),
        },
      },
      {
        perm: null,
        col: {
          id: 'actions',
          header: t('parcels.list.columns.actions'),
          enableSorting: false,
          cell: (info) => {
            const row = info.row.original;
            const isPending = row.status === 'PENDING';
            return (
              <div className="flex items-center gap-1.5">
                {canViewDetail && (
                  <IconButton
                    label={t('parcels.list.actions.view')}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/parcels/${row.id}`);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </IconButton>
                )}
                {isPending && canApprove && (
                  <IconButton
                    label={t('parcels.list.actions.approve')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setApproveTarget(row);
                    }}
                  >
                    <Check className="h-4 w-4 text-emerald-600" />
                  </IconButton>
                )}
                {isPending && canReject && (
                  <IconButton
                    label={t('parcels.list.actions.reject')}
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRejectTarget(row);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </IconButton>
                )}
                {canEdit && (
                  <IconButton
                    label={t('parcels.list.actions.edit')}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/parcels/${row.id}/edit`);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </IconButton>
                )}
                {canDelete && (
                  <IconButton
                    label={t('parcels.list.actions.delete')}
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(row);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                )}
              </div>
            );
          },
        },
      },
    ];
    return all
      .filter(({ perm }) => (perm === null ? true : hasPerm(permsRoot, perm)))
      .map(({ col }) => col);
  }, [
    navigate,
    t,
    permsRoot,
    canApprove,
    canReject,
    canEdit,
    canDelete,
    canViewDetail,
  ]);

  return (
    <>
      <PageHeader
        title={t('parcels.list.title')}
        subtitle={t('parcels.list.subtitle')}
        right={
          canCreate ? (
            <Button
              onClick={() => navigate('/parcels/create')}
              leftIcon={<PackagePlus className="h-4 w-4" />}
              title={t('parcels.list.newParcel')}
              aria-label={t('parcels.list.newParcel')}
            >
              <span className="hidden sm:inline">{t('parcels.list.newParcel')}</span>
            </Button>
          ) : null
        }
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={canFilterSearch ? search : ''}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('parcels.list.searchPlaceholder')}
          hideSearch={!canFilterSearch}
        >
          {canFilterStatus && (
            <div className="w-full sm:w-56">
              <Select
                options={STATUS_OPTIONS}
                value={status}
                onChange={(e) => {
                  setPage(1);
                  setStatus(e.target.value as ParcelStatus | '');
                }}
              />
            </div>
          )}
          <div className="w-full sm:w-56">
            <Select
              options={DEBT_OPTIONS}
              value={hasDebt}
              onChange={(e) => {
                setPage(1);
                setHasDebt(e.target.value as '' | 'true' | 'false');
              }}
            />
          </div>
        </FilterBar>

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('parcels.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={query.data?.results ?? []}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('parcels.list.emptyTitle')}
              emptyDescription={t('parcels.list.emptyDescription')}
              emptyIcon={<Package className="h-8 w-8" aria-hidden />}
              onRowClick={canViewDetail ? (row) => navigate(`/parcels/${row.id}`) : undefined}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={query.data?.count ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <FeaturesParcelsApproveDialog parcel={approveTarget} onClose={() => setApproveTarget(null)} />
      <FeaturesParcelsRejectDialog parcel={rejectTarget} onClose={() => setRejectTarget(null)} />
      <FeaturesParcelsDeleteDialog parcel={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </>
  );
};
